import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Loader2, User, Sparkles, Plus, MessageSquare, Trash2, Menu, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

// Common spelling corrections dictionary
const spellingFixes = {
  'teh': 'the', 'thier': 'their', 'recieve': 'receive', 'beleive': 'believe',
  'occured': 'occurred', 'untill': 'until', 'seperate': 'separate',
  'definately': 'definitely', 'goverment': 'government', 'arguement': 'argument',
  'occassion': 'occasion', 'neccessary': 'necessary', 'accomodate': 'accommodate',
  'acheive': 'achieve', 'accross': 'across', 'agressive': 'aggressive',
  'apparantly': 'apparently', 'basicly': 'basically', 'becuase': 'because',
  'begining': 'beginning', 'calender': 'calendar', 'catagory': 'category',
  'commited': 'committed', 'comparision': 'comparison', 'concious': 'conscious',
  'copywrite': 'copyright', 'critisism': 'criticism', 'curiousity': 'curiosity',
  'deceive': 'deceive', 'desparate': 'desperate', 'diffrent': 'different',
  'dissapear': 'disappear', 'dissapoint': 'disappoint', 'embarass': 'embarrass',
  'enviroment': 'environment', 'exagerate': 'exaggerate', 'excercise': 'exercise',
  'existance': 'existence', 'experiance': 'experience', 'facinating': 'fascinating',
  'finaly': 'finally', 'foriegn': 'foreign', 'fourty': 'forty', 'freind': 'friend',
  'gaurd': 'guard', 'happend': 'happened', 'harrass': 'harass', 'heighth': 'height',
  'heros': 'heroes', 'humourous': 'humorous', 'hygeine': 'hygiene', 'ignorence': 'ignorance',
  'imaginery': 'imaginary', 'immediatly': 'immediately', 'independant': 'independent',
  'innoculate': 'inoculate', 'intellegent': 'intelligent', 'intresting': 'interesting',
  'irresistable': 'irresistible', 'knowlege': 'knowledge', 'libary': 'library',
  'liscense': 'license', 'manuever': 'maneuver', 'medeval': 'medieval',
  'millenia': 'millennia', 'minature': 'miniature', 'mischevous': 'mischievous',
  'misspell': 'misspell', 'momento': 'memento', 'naturaly': 'naturally',
  'noticable': 'noticeable', 'occurence': 'occurrence', 'offical': 'official',
  'oportunity': 'opportunity', 'optimisim': 'optimism', 'orignal': 'original',
  'outragous': 'outrageous', 'parliment': 'parliament', 'particualr': 'particular',
  'passtime': 'pastime', 'perseverence': 'perseverance', 'persue': 'pursue',
  'posession': 'possession', 'potatos': 'potatoes', 'preceed': 'precede',
  'prefered': 'preferred', 'privelege': 'privilege', 'probaly': 'probably',
  'proffessor': 'professor', 'prominant': 'prominent', 'pronounciation': 'pronunciation',
  'publically': 'publicly', 'realy': 'really', 'reccomend': 'recommend',
  'refered': 'referred', 'relevent': 'relevant', 'religous': 'religious',
  'remeber': 'remember', 'repetative': 'repetitive', 'resistence': 'resistance',
  'restaraunt': 'restaurant', 'rythm': 'rhythm', 'sacrilegious': 'sacrilegious',
  'shedule': 'schedule', 'sieze': 'seize', 'similer': 'similar', 'sincerly': 'sincerely',
  'speach': 'speech', 'succesful': 'successful', 'supercede': 'supersede',
  'suprise': 'surprise', 'temperture': 'temperature', 'tendancy': 'tendency',
  'therefor': 'therefore', 'tomatos': 'tomatoes', 'tommorow': 'tomorrow',
  'tounge': 'tongue', 'truely': 'truly', 'tyrany': 'tyranny', 'underate': 'underrate',
  'unfortunatly': 'unfortunately', 'unnecesary': 'unnecessary', 'usualy': 'usually',
  'vaccuum': 'vacuum', 'vegatable': 'vegetable', 'vehical': 'vehicle',
  'visious': 'vicious', 'wether': 'whether', 'wierd': 'weird', 'writting': 'writing',
  'dont': "don't", 'cant': "can't", 'wont': "won't", 'didnt': "didn't",
  'doesnt': "doesn't", 'isnt': "isn't", 'wasnt': "wasn't", 'werent': "weren't",
  'havent': "haven't", 'hasnt': "hasn't", 'hadnt': "hadn't", 'wouldnt': "wouldn't",
  'couldnt': "couldn't", 'shouldnt': "shouldn't", 'im': "I'm", 'ive': "I've",
  'youre': "you're", 'youve': "you've", 'theyre': "they're", 'theyve': "they've",
  'weve': "we've", 'id': "I'd", 'youd': "you'd", 'hed': "he'd", 'shed': "she'd",
  'wed': "we'd", 'theyd': "they'd", 'ill': "I'll", 'youll': "you'll",
  'itll': "it'll", 'theyll': "they'll", 'lets': "let's",
  'heres': "here's", 'theres': "there's", 'wheres': "where's", 'whats': "what's",
  'whos': "who's", 'hows': "how's", 'thats': "that's"
};

// Real-time auto-correct as user types
const autoCorrectWhileTyping = (text) => {
  if (!text) return text;
  
  let corrected = text;
  
  // Capitalize first letter
  if (corrected.length > 0) {
    corrected = corrected.charAt(0).toUpperCase() + corrected.slice(1);
  }
  
  // Capitalize 'i' when standalone (surrounded by spaces or at boundaries)
  corrected = corrected.replace(/(\s|^)i(\s|$)/g, '$1I$2');
  corrected = corrected.replace(/(\s|^)i([''])/g, '$1I$2');
  
  // Fix spelling for completed words (followed by space or punctuation)
  Object.entries(spellingFixes).forEach(([wrong, right]) => {
    const regex = new RegExp(`\\b${wrong}\\b(?=\\s|[.,!?;:]|$)`, 'gi');
    corrected = corrected.replace(regex, (match) => {
      if (match[0] === match[0].toUpperCase()) {
        return right.charAt(0).toUpperCase() + right.slice(1);
      }
      return right;
    });
  });
  
  return corrected;
};

// Final auto-correct when sending (adds punctuation)
const autoCorrectOnSend = (text) => {
  if (!text || !text.trim()) return text;
  
  let corrected = autoCorrectWhileTyping(text.trim());
  
  // Add period at end if no punctuation
  if (!/[.!?]$/.test(corrected)) {
    corrected += '.';
  }
  
  return corrected;
};

const ChatPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentTitle, setCurrentTitle] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const hasSentMessage = useRef(false);

  // Auto-focus textarea when AI finishes responding (after user sent a message)
  useEffect(() => {
    if (!loading && hasSentMessage.current && textareaRef.current) {
      // Focus the textarea so user can type immediately
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Also click it programmatically for mobile browsers
          textareaRef.current.click();
        }
      }, 200);
    }
  }, [loading]);

  // Auto-resize textarea as user types
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  useEffect(() => {
    loadConversations();
    startNewChat();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Refocus on input after scroll
    setTimeout(() => {
      if (textareaRef.current && !loading) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations');
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setCurrentTitle(null);
    setMessages([{
      role: 'assistant',
      content: `Hello${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! I'm Professor Clio, your AI history guide. Ask me anything about history - from ancient civilizations to modern events. What would you like to explore today?`
    }]);
  };

  const loadConversation = async (session) => {
    try {
      const response = await axios.get(`${API}/chat/history/${session.session_id}`, { withCredentials: true });
      setSessionId(session.session_id);
      setCurrentTitle(session.title);
      setMessages(response.data.messages || []);
      
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const deleteConversation = async (sessionIdToDelete, e) => {
    e.stopPropagation();
    
    if (!confirm('Delete this conversation?')) return;
    
    try {
      await axios.delete(`${API}/chat/history/${sessionIdToDelete}`, { withCredentials: true });
      
      if (sessionIdToDelete === sessionId) {
        startNewChat();
      }
      
      loadConversations();
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = autoCorrectOnSend(input.trim());
    setInput('');
    hasSentMessage.current = true; // Mark that user has sent a message
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage, session_id: sessionId },
        { withCredentials: true }
      );

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      
      if (response.data.title) {
        setCurrentTitle(response.data.title);
        loadConversations();
      }
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter((_, i) => i !== prev.length - 1));
    } finally {
      setLoading(false);
      // Keep focus on input for continuous conversation flow
      // Use setTimeout to ensure React has re-rendered with loading=false
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 50);
    }
  };

  return (
    <div className="flex h-screen theme-bg overflow-hidden">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed z-50 w-72 h-full transition-transform duration-300 ${isDarkMode ? 'bg-gray-900/95 border-amber-900/50' : 'bg-white border-amber-200'} backdrop-blur-sm border-r flex flex-col`}>
        {/* Sidebar Header */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-amber-900/50' : 'border-amber-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
              <span className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>History Chat</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className={`h-8 w-8 ${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={startNewChat}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Conversation
          </Button>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {conversations.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className={`w-10 h-10 ${isDarkMode ? 'text-amber-500/50' : 'text-amber-400'} mx-auto mb-3`} />
              <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>No conversations yet</p>
              <p className={`text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'} mt-1`}>Start chatting to create one</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.session_id}
                onClick={() => loadConversation(conv)}
                className={`group p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                  conv.session_id === sessionId 
                    ? `${isDarkMode ? 'bg-amber-600/30 border-amber-500/50' : 'bg-amber-100 border-amber-300'} shadow-sm border` 
                    : `${isDarkMode ? 'hover:bg-amber-900/30' : 'hover:bg-amber-50'} border border-transparent`
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    conv.session_id === sessionId 
                      ? 'bg-amber-500 text-white' 
                      : isDarkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-200 text-amber-700'
                  }`}>
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium truncate ${
                      conv.session_id === sessionId 
                        ? isDarkMode ? 'text-amber-100' : 'text-amber-900'
                        : isDarkMode ? 'text-amber-200' : 'text-amber-800'
                    }`}>
                      {conv.title || 'Untitled'}
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'} mt-0.5`}>
                      {conv.message_count} messages · {new Date(conv.updated_at || conv.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'text-amber-400 hover:text-red-400 hover:bg-red-900/30' : 'text-amber-600 hover:text-red-600 hover:bg-red-50'}`}
                    onClick={(e) => deleteConversation(conv.session_id, e)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Back to Dashboard */}
        <div className={`p-3 border-t ${isDarkMode ? 'border-amber-900/50' : 'border-amber-200'}`}>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className={`w-full justify-start ${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-black/70 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b px-4 py-3 flex items-center gap-4`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className={`h-9 w-9 ${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} truncate`}>
                {currentTitle || 'Professor Clio'}
              </h1>
              <p className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Your AI History Guide</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                {/* Avatar */}
                {message.role === 'user' ? (
                  user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt="You" 
                      className="w-8 h-8 rounded-xl flex-shrink-0 shadow-sm object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br from-amber-500 to-amber-600">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )
                ) : (
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br from-violet-500 to-purple-600">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                
                {/* Message Bubble */}
                <div className={`min-w-0 max-w-[90%] sm:max-w-[85%] ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                  <div className={`px-4 py-3 rounded-2xl overflow-hidden ${
                    message.role === 'user' 
                      ? 'bg-amber-500 text-white rounded-tr-md' 
                      : 'bg-white shadow-sm border border-amber-300 text-gray-800 rounded-tl-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading Indicator */}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white shadow-sm border border-amber-300 rounded-2xl rounded-tl-md px-4 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className={`border-t ${isDarkMode ? 'border-amber-900/50 bg-black/70' : 'border-amber-200 bg-white/80'} backdrop-blur-sm p-4`}>
          <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
            <div className="flex gap-3 items-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className={`h-10 px-3 self-center ${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-xl flex-shrink-0`}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div className="flex-1 flex gap-3 items-end bg-white rounded-2xl border border-gray-200 shadow-sm p-2 focus-within:border-amber-300 focus-within:ring-2 focus-within:ring-amber-100 transition-all">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    const textarea = e.target;
                    const cursorPos = textarea.selectionStart;
                    const originalValue = e.target.value;
                    const corrected = autoCorrectWhileTyping(originalValue);
                    const diff = corrected.length - originalValue.length;
                    
                    setInput(corrected);
                    adjustTextareaHeight();
                    
                    // Restore cursor position after React re-render
                    requestAnimationFrame(() => {
                      if (textareaRef.current) {
                        const newPos = cursorPos + diff;
                        textareaRef.current.setSelectionRange(newPos, newPos);
                      }
                    });
                  }}
                  placeholder="Ask about any historical topic..."
                  className="flex-1 px-3 py-2 text-base bg-transparent border-0 focus:outline-none focus:ring-0 resize-none min-h-[40px] max-h-[200px] placeholder:text-gray-400 text-gray-900"
                  disabled={loading}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-form-type="other"
                  data-lpignore="true"
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (input.trim() && !loading) {
                        sendMessage(e);
                      }
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  disabled={loading || !input.trim()}
                  size="icon"
                  className="h-10 w-10 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'} text-center mt-2`}>
              Press Enter to send · Shift+Enter for new line
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
